import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Send, PackageCheck, Zap, X, Mail } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  sent: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  received: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/purchase-orders");
    setPos(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const autoGen = async () => {
    setBusy(true);
    try {
      const r = await api.post("/purchase-orders/auto-generate");
      if (r.data.count === 0)
        toast.info("No low-stock items with suppliers to reorder");
      else toast.success(`Generated ${r.data.count} draft PO(s)`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const openPO = (po) => {
    setSelected(po);
    setDraft(po.email_draft || "");
  };

  const generateDraft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const r = await api.post("/ai/po-email-draft", { po_id: selected.id });
      setDraft(r.data.email_draft);
      toast.success("AI draft ready");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setDrafting(false);
  };

  const sendPO = async () => {
    if (!selected) return;
    if (!selected.supplier_email) {
      toast.error("This supplier has no email on file");
      return;
    }
    setBusy(true);
    try {
      const body = draft && draft.trim().length > 0 ? { email_body: draft } : {};
      const r = await api.post(`/purchase-orders/${selected.id}/send`, body);
      if (r.data._delivery === "resend") {
        toast.success(`Email sent to ${selected.supplier_email} via Resend`);
      } else if (r.data._delivery_error) {
        toast.error(`Resend error: ${r.data._delivery_error}`);
      } else {
        toast.success("PO marked as sent (email mocked — set RESEND_API_KEY to send)");
      }
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const markReceived = async (po) => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${po.id}/mark-received`);
      toast.success("PO received. Stock updated.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="pos-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
            Ordering
          </div>
          <h1 className="font-display text-4xl font-bold">Purchase Orders</h1>
          <p className="text-slate-500 text-sm mt-1">
            Automate reorders when stock hits reorder points.
          </p>
        </div>
        <button onClick={autoGen} disabled={busy} className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60" data-testid="auto-generate-btn">
          <Zap className="w-4 h-4" /> Auto-generate from low stock
        </button>
      </div>

      <div className="surface-card rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-slate-500 text-sm">Loading…</div>
        ) : pos.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <div className="font-display text-2xl mb-2">No purchase orders</div>
            <p className="text-sm">Click "Auto-generate" once you have low-stock items.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-widest">
                <th className="text-left px-6 py-3">PO #</th>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-right px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pos.map((po) => (
                <tr key={po.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`row-po-${po.id}`}>
                  <td className="px-6 py-3 font-mono-plex text-xs text-amber-400">
                    {po.po_number}
                  </td>
                  <td className="px-4 py-3">
                    <div>{po.supplier_name}</div>
                    {po.supplier_email && (
                      <div className="text-xs text-slate-500 font-mono-plex">
                        {po.supplier_email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-plex text-slate-400">
                    {po.items.length}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-plex text-amber-500">
                    ${Number(po.total).toFixed(2)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${STATUS_STYLES[po.status]} capitalize font-mono-plex`}>
                      {po.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openPO(po)} className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/[0.03]" data-testid={`view-po-${po.id}`}>
                        View
                      </button>
                      {po.status === "sent" && (
                        <button onClick={() => markReceived(po)} className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1" data-testid={`receive-po-${po.id}`}>
                          <PackageCheck className="w-3 h-3" /> Receive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-2xl bg-[#12141a] border border-white/10 rounded-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="po-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-2xl font-bold">{selected.po_number}</div>
                <div className="text-xs text-slate-500 font-mono-plex mt-1">
                  {selected.supplier_name} · {selected.supplier_email || "no email"}
                </div>
              </div>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div className="border border-white/5 rounded-md">
              {selected.items.map((it, i) => (
                <div key={`${it.item_id}-${i}`} className="flex justify-between px-4 py-2 border-b border-white/5 last:border-b-0 text-sm">
                  <span>{it.item_name}</span>
                  <span className="font-mono-plex text-slate-400">
                    {it.quantity} × ${Number(it.unit_cost).toFixed(2)} = ${(it.quantity * it.unit_cost).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between px-4 py-2 bg-white/[0.02] font-mono-plex text-sm">
                <span className="text-slate-400">Total</span>
                <span className="text-amber-500 font-semibold">${Number(selected.total).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex flex items-center gap-2">
                  <Mail className="w-3 h-3" /> Email to supplier
                </div>
                <button onClick={generateDraft} disabled={drafting} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1 disabled:opacity-60" data-testid="ai-draft-btn">
                  <Sparkles className="w-3 h-3" /> {drafting ? "Drafting…" : "AI draft"}
                </button>
              </div>
              <textarea
                rows="8"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Click 'AI draft' to generate an email, or leave blank to send a default body."
                className="w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500 font-mono-plex"
                data-testid="po-email-body"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm">
                Close
              </button>
              {selected.status !== "received" && (
                <button onClick={sendPO} disabled={busy} className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60" data-testid="send-po-btn">
                  <Send className="w-4 h-4" /> Send PO
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
